import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

const CERT_ADMIN_ROLE = 'cert.admin';
const CERT_SUPER_ADMIN_ROLE = 'cert.superAdmin';
const CERT_USER_ROLE = 'cert.user';

@Injectable({
  providedIn: 'root'
})
export class RoleViewService {
  private readonly viewRoleSubject = new BehaviorSubject<string>('');
  private readonly actualRoleSubject = new BehaviorSubject<string>('');
  private readonly workdayIdSubject = new BehaviorSubject<string>('');
  private readonly userIdSubject = new BehaviorSubject<string>('');
  private readonly userNameSubject = new BehaviorSubject<string>('');
  private readonly userEmailSubject = new BehaviorSubject<string>('');
  private readonly userUuidSubject = new BehaviorSubject<string>('');


  constructor() {
    this.reinitialize();
  }

  private initializeWorkdayId() {
    const workdayId = this.getActualWorkdayId();
    sessionStorage.setItem('CERT_WORKDAY_ID', workdayId);
    this.workdayIdSubject.next(workdayId);
  }
  private initializeRoles() {
    const actualRole = this.getActualUserRole();
    this.getAdminAsRole();
    this.actualRoleSubject.next(actualRole);

    // Load saved view role from sessionStorage, default to actual role
    const savedViewRole = sessionStorage.getItem('CERT_VIEW_ROLE');
    this.viewRoleSubject.next(savedViewRole || actualRole);
  }

  private initializeUserInfo(): void {
    const userName = this.getActualUserName();
    const userEmail = this.getActualUserEmail();
    const userUuid = this.getActualUserUuid();
    this.userNameSubject.next(userName);
    this.userEmailSubject.next(userEmail);
    this.userUuidSubject.next(userUuid);
  }
  getWorkdayId(): string {
    const workdayId = sessionStorage.getItem('CERT_WORKDAY_ID');
    return workdayId ?? '';
  }
  getAdminAsRole(): string {
    const tokenStr = sessionStorage.getItem('CERT_IDTOKEN');
    if (tokenStr) {
      try {
        const decoded: any = JSON.parse(tokenStr);
        if (decoded.roles && Array.isArray(decoded.roles) && decoded.roles.length > 0) {
          const adminRole = decoded.roles.find((r: string) =>
            r.toLowerCase() === CERT_ADMIN_ROLE
          );

          if (adminRole) {
            return 'admin';
          }
        }
      } catch (e) {
        console.trace('Error parsing admin role:', e);
        return '';
      }
    }
    return '';
  }
  
  getJobTitle(): string {
    try {
      const idToken = sessionStorage.getItem('CERT_IDTOKEN');
      if (!idToken) {
        return '';
      }
      const decodedToken = JSON.parse(idToken);
      return decodedToken['Job Title'] ?? decodedToken.job_title ?? decodedToken.jobTitle ?? '';
    } catch (error) {
      console.trace('Error parsing job title:', error);
      return '';
    }
  }

  private getActualUserRole(): string {
    const tokenStr = sessionStorage.getItem('CERT_IDTOKEN');
    if (tokenStr) {
      try {
        const decoded: any = JSON.parse(tokenStr);
        if (decoded.roles && Array.isArray(decoded.roles) && decoded.roles.length > 0) {
          const superAdminRole = decoded.roles.find((r: string) =>
            r.toLowerCase() === CERT_SUPER_ADMIN_ROLE.toLowerCase()
          );
          if (superAdminRole) {
            return CERT_SUPER_ADMIN_ROLE;
          }
          const adminRole = decoded.roles.find((r: string) =>
            r.toLowerCase() === CERT_ADMIN_ROLE.toLowerCase()
          );
          if (adminRole) {
            return CERT_ADMIN_ROLE;
          }
          return decoded.roles[0];
        }
        return decoded.userRole || decoded.role || CERT_USER_ROLE;
      } catch (e) {
        console.trace('Error parsing admin role:', e);
        return CERT_USER_ROLE;
      }
    }
    return 'user';
  }
  private getActualWorkdayId(): string {
    const tokenStr = sessionStorage.getItem('CERT_IDTOKEN');
    if (tokenStr) {
      try {
        const decoded: any = JSON.parse(tokenStr);
        if (decoded.WorkdayID && decoded.WorkdayID.length > 0) {
          return decoded.WorkdayID;
        }
        return decoded.WorkdayID || '';
      } catch (e) {
        console.trace('Error parsing WorkdayID:', e);
        return '';
      }
    }
    return '';
  }

  private getActualUserName(): string {
    const tokenStr = sessionStorage.getItem('CERT_IDTOKEN');
    if (tokenStr) {
      try {
        const decoded: any = JSON.parse(tokenStr);
        return decoded.name || '';
      } catch (e) {
        console.trace('Error parsing user name:', e);
        return '';
      }
    }
    return '';
  }

  private getActualUserEmail(): string {
    const tokenStr = sessionStorage.getItem('CERT_IDTOKEN');
    if (tokenStr) {
      try {
        const decoded: any = JSON.parse(tokenStr);
        return decoded.email || '';
      } catch (e) {
        console.trace('Error parsing user email:', e);
        return '';
      }
    }
    return '';
  }

  private getActualUserUuid(): string {
    const tokenStr = sessionStorage.getItem('CERT_IDTOKEN');
    if (tokenStr) {
      try {
        const decoded: any = JSON.parse(tokenStr);
        return decoded.GUID || decoded.uuid || '';
      } catch (e) {
        console.trace('Error parsing user UUID:', e);
        return '';
      }
    }
    return '';
  }
  setWorkdayId(workdayId: string): void {
    this.workdayIdSubject.next(workdayId);
    sessionStorage.setItem('CERT_WORKDAY_ID', workdayId);
  }
  getWorkdayId$(): Observable<string> {
    return this.workdayIdSubject.asObservable();
  }


  getActualRole$(): Observable<string> {
    return this.actualRoleSubject.asObservable();
  }

  getViewRole$(): Observable<string> {
    return this.viewRoleSubject.asObservable();
  }

  getViewRole(): string {
    return this.viewRoleSubject.value;
  }

  getActualRole(): string {
    return this.actualRoleSubject.value;
  }

  setViewRole(role: string): void {
    this.viewRoleSubject.next(role);
    sessionStorage.setItem('CERT_VIEW_ROLE', role);
  }

  resetViewRole(): void {
    const actualRole = this.actualRoleSubject.value;
    this.viewRoleSubject.next(actualRole);
    sessionStorage.setItem('CERT_VIEW_ROLE', actualRole);
  }

  getAvailableRoles(): string[] {
    const actualRole = this.actualRoleSubject.value;

    if (actualRole === CERT_SUPER_ADMIN_ROLE) {
      return [CERT_SUPER_ADMIN_ROLE, CERT_USER_ROLE];
    } else if (actualRole === CERT_ADMIN_ROLE) {
      return [CERT_ADMIN_ROLE, CERT_USER_ROLE];
    }
    return [CERT_USER_ROLE];
  }

  canToggleRole(): boolean {
    return this.getAvailableRoles().length > 1;
  }

  getUserName$(): Observable<string> {
    return this.userNameSubject.asObservable();
  }

  getUserEmail$(): Observable<string> {
    return this.userEmailSubject.asObservable();
  }

  getUserUuid$(): Observable<string> {
    return this.userUuidSubject.asObservable();
  }

  getUserName(): string {
    return this.userNameSubject.value;
  }

  getUserEmail(): string {
    return this.userEmailSubject.value;
  }

  getUserUuid(): string {
    return this.userUuidSubject.value;
  }

  setUserName(name: string): void {
    this.userNameSubject.next(name);
  }

  setUserEmail(email: string): void {
    this.userEmailSubject.next(email);
  }

  setUserUuid(uuid: string): void {
    this.userUuidSubject.next(uuid);
  }

  reinitialize(): void {
    this.initializeUserInfo();
    this.initializeWorkdayId();
    this.initializeRoles();
  }
}
